import { useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const OVERALL_STAGE_ID = "__overall__";

export interface ChampionshipStage {
  id: string;
  championship_id: string;
  name: string;
  sort_order: number;
  is_default: boolean;
}

/**
 * Manages the currently-active championship stage. Mirrors useActiveCategory.
 *
 * - Reads/writes `?stage=<id>` in the URL.
 * - Persists per championship in sessionStorage.
 * - When 2+ real stages exist, prepends a virtual "Classificação Geral" stage
 *   with id OVERALL_STAGE_ID. That virtual stage is the default in that case.
 */
export function useActiveStage(championshipId: string | null | undefined) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlStage = searchParams.get("stage");

  const { data: rawStages = [], isLoading } = useQuery<ChampionshipStage[]>({
    queryKey: ["championship-stages", championshipId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("championship_stages" as any)
        .select("*")
        .eq("championship_id", championshipId!)
        .order("sort_order", { ascending: true })
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data || []) as unknown as ChampionshipStage[];
    },
    enabled: !!championshipId,
  });

  const hasMultiple = rawStages.length > 1;

  const stages = useMemo<ChampionshipStage[]>(() => {
    if (!hasMultiple) return rawStages;
    const virtual: ChampionshipStage = {
      id: OVERALL_STAGE_ID,
      championship_id: championshipId || "",
      name: "Classificação Geral",
      sort_order: -1,
      is_default: false,
    };
    return [virtual, ...rawStages];
  }, [rawStages, hasMultiple, championshipId]);

  const defaultStage = useMemo(() => {
    if (hasMultiple) return stages[0]; // Classificação Geral
    return rawStages.find((s) => s.is_default) ?? rawStages[0] ?? null;
  }, [stages, rawStages, hasMultiple]);

  const storageKey = championshipId ? `activeStage:${championshipId}` : null;
  const storedStage =
    typeof window !== "undefined" && storageKey
      ? window.sessionStorage.getItem(storageKey)
      : null;

  const activeStage = useMemo(() => {
    if (urlStage) {
      const match = stages.find((s) => s.id === urlStage);
      if (match) return match;
    }
    if (storedStage) {
      const match = stages.find((s) => s.id === storedStage);
      if (match) return match;
    }
    return defaultStage;
  }, [urlStage, storedStage, stages, defaultStage]);

  useEffect(() => {
    if (!storageKey || !activeStage) return;
    try {
      window.sessionStorage.setItem(storageKey, activeStage.id);
    } catch {
      // ignore
    }
  }, [storageKey, activeStage]);

  useEffect(() => {
    if (!urlStage || stages.length === 0) return;
    const exists = stages.some((s) => s.id === urlStage);
    if (!exists) {
      const next = new URLSearchParams(searchParams);
      next.delete("stage");
      setSearchParams(next, { replace: true });
    }
  }, [urlStage, stages, searchParams, setSearchParams]);

  const setActiveStage = useCallback(
    (id: string) => {
      if (storageKey) {
        try {
          window.sessionStorage.setItem(storageKey, id);
        } catch {
          // ignore
        }
      }
      const next = new URLSearchParams(searchParams);
      if (defaultStage && id === defaultStage.id) {
        next.delete("stage");
      } else {
        next.set("stage", id);
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams, defaultStage, storageKey],
  );

  const isOverall = activeStage?.id === OVERALL_STAGE_ID;

  /**
   * stage_id to use when INSERTING new rows. When in "overall" mode, falls back
   * to the default real stage so we never persist NULL or the virtual id.
   */
  const writeStageId =
    isOverall
      ? (rawStages.find((s) => s.is_default)?.id ?? rawStages[0]?.id ?? null)
      : (activeStage?.id ?? null);

  return {
    stages,
    rawStages,
    activeStage,
    activeStageId: activeStage?.id ?? null,
    /** stage_id to use in DB filters: null when "overall" (means: don't filter by stage) */
    effectiveStageId: isOverall ? null : (activeStage?.id ?? null),
    writeStageId,
    isOverall,
    setActiveStage,
    isLoading,
    hasMultiple,
  };
}